/**
 * One decision, consulted by BOTH GraphQL capture layers: should a failure that
 * ran without a JWT be reported to GlitchTip?
 *
 * A request that goes out with no Authorization header runs as Hasura's `public`
 * role, which has no permissions — so Hasura answers "field 'x' not found in
 * type: 'mutation_root'", "missing session variable", or a masked generic error.
 * None of those can be a real backend bug: the authenticated retry hits the same
 * code path and is captured there if it genuinely breaks.
 *
 * ⛔ The Apollo `errorLink` already had this guard and returned early — but
 * service-layer calls go through `gqlRequest`, which captures the SAME failure
 * from its own catch block. The guard therefore logged "Skipping capture ..."
 * into the breadcrumbs of the very issue it was meant to prevent (GlitchTip
 * #2402, CreateReservationContract from /dashboard/calendar). A guard that only
 * covers one of two capture paths suppresses nothing. Both layers must consult
 * this module — do not re-inline the decision.
 */

/**
 * How long a transport-level "this ran unauthenticated" note stays readable by
 * the service layer. The errorLink fires microseconds before `gqlRequest`'s
 * catch block; this only has to survive that hop, and expiring quickly keeps a
 * stale note from muting a genuinely-authenticated later failure.
 */
const NOTE_TTL_MS = 15_000

const ANONYMOUS = '__anonymous_operation__'

/** operation name -> timestamp of the unauthenticated failure the transport saw. */
const notes = new Map<string, number>()

function keyOf(operationName?: string | null): string {
  return operationName || ANONYMOUS
}

function prune(now: number): void {
  for (const [key, at] of notes) {
    if (now - at > NOTE_TTL_MS) notes.delete(key)
  }
}

/**
 * The single rule. `ranUnauthenticated` means the request carried no JWT;
 * `isAuthFailure` means Hasura reported `invalid-jwt`, which must stay
 * reportable because it drives the sign-out redirect.
 */
export function shouldSkipUnauthenticatedCapture(input: {
  ranUnauthenticated: boolean
  isAuthFailure: boolean
}): boolean {
  return input.ranUnauthenticated && !input.isAuthFailure
}

/**
 * Record, at the transport, that this operation failed on an unauthenticated
 * request — so the service layer above can suppress its own duplicate capture.
 */
export function markUnauthenticatedFailure(operationName?: string | null): void {
  const now = Date.now()
  prune(now)
  notes.set(keyOf(operationName), now)
}

/**
 * Read and clear the transport's note for this operation. Returns false when
 * there is none, or when it has aged past {@link NOTE_TTL_MS}.
 */
export function consumeUnauthenticatedFailure(operationName?: string | null): boolean {
  const now = Date.now()
  prune(now)
  const key = keyOf(operationName)
  const at = notes.get(key)
  if (at === undefined) return false
  notes.delete(key)
  return now - at <= NOTE_TTL_MS
}

/** Test seam: forget every recorded note. */
export function resetUnauthenticatedFailures(): void {
  notes.clear()
}
